// Lectura del comprobante del proveedor (factura del SRI) para el formulario de Egresos.
//
// Espeja backend/models.py (taxBucketsFromReceiptXML, receiptLinesFromXML): el impuesto
// se distingue por su `codigo` (2 IVA, 3 ICE, 5 IRBPNR) y la tarifa del IVA por su
// `codigoPorcentaje`. Antes se miraba solo el codigoPorcentaje: el ICE y el IRBPNR no
// entraban en ningún lado y la compra no sumaba a su total.
import Big from "big.js";

const IMPUESTO_IVA = "2";
const IMPUESTO_ICE = "3";
const IMPUESTO_IRBPNR = "5";
// codigoPorcentaje del IVA que no paga: 0%, no objeto y exento.
const IVA_SIN_TARIFA = ["0", "6", "7"];
const IVA_5 = "5";

function text(node, tag) {
    const element = node.getElementsByTagName(tag)[0];
    return element && element.textContent ? element.textContent.trim() : "";
}

function amount(value) {
    try {
        return new Big(value === "" ? "0" : value);
    } catch (e) {
        return new Big("0");
    }
}

// Totales de la cabecera (<totalConImpuestos>). sub12 es la base gravada LIMPIA: la del
// XML ya trae el ICE sumado (cascada), así que se le resta, igual que en el backend.
export function receiptTotals(infoFactura) {
    const totals = {
        sub0: new Big("0"), sub5: new Big("0"), sub15: new Big("0"), sub12: new Big("0"),
        iceValue: new Big("0"), irbpnrValue: new Big("0"), tax: new Big("0"),
    };
    const container = infoFactura.getElementsByTagName("totalConImpuestos")[0];
    if (!container) return totals;

    let taxed = new Big("0");
    const taxes = container.getElementsByTagName("totalImpuesto");
    for (let i = 0; i < taxes.length; i++) {
        const codigo = text(taxes[i], "codigo");
        const codigoPorcentaje = text(taxes[i], "codigoPorcentaje");
        const base = amount(text(taxes[i], "baseImponible"));
        const valor = amount(text(taxes[i], "valor"));
        if (codigo === IMPUESTO_ICE) {
            totals.iceValue = totals.iceValue.plus(valor);
        } else if (codigo === IMPUESTO_IRBPNR) {
            totals.irbpnrValue = totals.irbpnrValue.plus(valor);
        } else if (codigo === IMPUESTO_IVA) {
            if (IVA_SIN_TARIFA.includes(codigoPorcentaje)) {
                totals.sub0 = totals.sub0.plus(base);
            } else {
                taxed = taxed.plus(base);
                if (codigoPorcentaje === IVA_5) totals.sub5 = totals.sub5.plus(base);
                totals.tax = totals.tax.plus(valor);
            }
        }
    }
    // El ICE grava bienes con IVA: se despeja de la base gravada total.
    totals.sub12 = taxed.minus(totals.iceValue);
    if (totals.sub12.lt(0)) totals.sub12 = new Big("0");
    totals.sub15 = totals.sub12.minus(totals.sub5);
    if (totals.sub15.lt(0)) totals.sub15 = new Big("0");
    return totals;
}

// Líneas del comprobante en el orden del XML. `line` es su posición entre TODOS los
// <detalle>: con ese índice el backend ubica la línea de un ítem enlazado, así que no se
// corre aunque una línea venga incompleta.
export function receiptLines(xml) {
    const container = xml.getElementsByTagName("detalles")[0];
    if (!container) return [];

    const nodes = container.getElementsByTagName("detalle");
    const lines = [];
    for (let i = 0; i < nodes.length; i++) {
        let iceValue = new Big("0");
        let irbpnrValue = new Big("0");
        let ivaCode = null;
        const taxes = nodes[i].getElementsByTagName("impuesto");
        for (let j = 0; j < taxes.length; j++) {
            const codigo = text(taxes[j], "codigo");
            const valor = amount(text(taxes[j], "valor"));
            if (codigo === IMPUESTO_ICE) iceValue = iceValue.plus(valor);
            else if (codigo === IMPUESTO_IRBPNR) irbpnrValue = irbpnrValue.plus(valor);
            else if (codigo === IMPUESTO_IVA) ivaCode = text(taxes[j], "codigoPorcentaje");
        }
        const hasIva = ivaCode !== null && !IVA_SIN_TARIFA.includes(ivaCode);
        lines.push({
            line: i,
            codigoPrincipal: text(nodes[i], "codigoPrincipal") || text(nodes[i], "codigoInterno"),
            codigoAuxiliar: text(nodes[i], "codigoAuxiliar") || text(nodes[i], "codigoAdicional"),
            descripcion: text(nodes[i], "descripcion"),
            cantidad: amount(text(nodes[i], "cantidad")),
            precioUnitario: amount(text(nodes[i], "precioUnitario")),
            descuento: amount(text(nodes[i], "descuento")),
            precioTotalSinImpuesto: amount(text(nodes[i], "precioTotalSinImpuesto")),
            iceValue: iceValue,
            irbpnrValue: irbpnrValue,
            impuesto: hasIva,
            // Tarifa para un producto nuevo creado desde la línea: las vigentes del selector
            // (0, 5 y 15 %); una histórica (12, 14 %) o la de turismo pasa a la general.
            ivaCode: !hasIva ? "0" : (ivaCode === IVA_5 ? IVA_5 : "4"),
        });
    }
    return lines;
}

// Nombre propuesto para un producto nuevo creado desde una línea: sin el empaque («Pet»,
// «x 24») y, si viene todo en mayúsculas, solo con la inicial. La persona lo corrige.
export function suggestedProductName(description) {
    let name = (description || "")
        .replace(/\bx\s*\d+\b/gi, " ")
        .replace(/\bpet\b/gi, " ")
        .replace(/\s+/g, " ")
        .trim();
    if (name && name === name.toUpperCase()) {
        name = name.charAt(0) + name.slice(1).toLowerCase();
    }
    return name;
}

// Unidades del negocio por artículo del proveedor, para una línea que ya se enlazó en una
// compra anterior (`link` es lo que responde dashboard/purchase/supplier-links). El
// proveedor no siempre factura en la misma unidad: Tesalia vende el agua «x 30» a veces
// por paca (1 a $5.22) y a veces por botella (15 a $0.174). Si el precio por artículo
// cambió a más del doble o a menos de la mitad, cambió la unidad: las unidades se escalan
// con el precio y el ítem queda para revisar su cantidad.
export function suggestedUnits(line, link) {
    const units = new Big(link.unitsPerSupplierUnit || '1');
    if (!link.supplierUnitPrice || line.cantidad.lte(0)) return { units: units, review: false };
    const before = new Big(link.supplierUnitPrice);
    const now = line.precioTotalSinImpuesto.div(line.cantidad);
    if (before.lte(0) || now.lte(0)) return { units: units, review: false };
    const ratio = now.div(before);
    if (ratio.gte('0.5') && ratio.lte('2')) return { units: units, review: false };
    return { units: units.times(ratio), review: true };
}

// Datos del ítem de inventario enlazado a una línea. Su costo es el de la línea: el
// subtotal más el ICE y el IRBPNR, que el comprador asume. Lo único que se decide es
// cuántas unidades del negocio son: `units` traduce la unidad del proveedor (una paca
// «x 30» que se cuenta por botella = 30). Con `review` la cantidad es una estimación
// (ver suggestedUnits): se redondea a unidades enteras y el ítem queda marcado.
export function linkedItemData(line, product, units, review = false) {
    let total = line.cantidad.times(units).round(6);
    if (review) total = total.gte(1) ? total.round(0) : total.round(2);
    return {
        productId: product.id,
        code: product.code,
        product: product.name,
        productObj: product,
        hasTax: product.hasTax,
        ivaCode: product.ivaCode,
        iceCode: product.iceCode,
        discount: 0.0,
        amount: total,
        receiptLine: line.line,
        lineSubtotal: line.precioTotalSinImpuesto,
        iceValue: line.iceValue,
        irbpnrValue: line.irbpnrValue,
        needsReview: review,
    };
}
